import type { ReactNode, SelectHTMLAttributes } from "react";
import { useFieldControl } from "./Field";
import { cx } from "./utils";

/** One option in a `Select`'s `options` array. */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Declarative options; ignored when `children` are provided. */
  options?: readonly SelectOption[];
  /** Leading disabled option shown when the value is empty. */
  placeholder?: string;
  children?: ReactNode;
}

/** Native `<select>` styled with tokens. Picks up aria wiring from `Field`. */
export function Select({ options, placeholder, className, children, ...rest }: SelectProps) {
  const field = useFieldControl();
  return (
    <div className={cx("xz-select", className)}>
      <select className="xz-select__el" {...field} {...rest}>
        {placeholder != null && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children ??
          options?.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
      </select>
      <span className="xz-select__caret" aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9.5 12 15.5 18 9.5" />
        </svg>
      </span>
    </div>
  );
}
